import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Maximize2, Landmark, Languages } from "lucide-react";
import { dignitaries, navLinks } from "../data/mock";
import { useT } from "../i18n";
import BrandLogo from "./BrandLogo";

function Person({ p }) {
  return (
    <div data-testid={`dignitary-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="dignitary">
      <div className="dignitary-photo">
        <img src={p.img} alt={p.name} />
      </div>
      <div className="dignitary-name">{p.name}</div>
      <div className="dignitary-role">{p.role}</div>
    </div>
  );
}

function ApEmblem({ className = "", testId }) {
  return (
    <div className={`logo-plate header-logo-slot ${className}`}>
      <img
        src="/img/ap-emblem.png"
        alt="Government of Andhra Pradesh"
        data-testid={testId}
        className="ap-state-emblem"
      />
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const [zoom, setZoom] = React.useState(1);
  const { lang, setLang, t } = useT();
  const location = useLocation();
  React.useEffect(() => setOpen(false), [location.pathname]);
  React.useEffect(() => {
    document.documentElement.style.setProperty("--ui-scale", String(zoom));
    document.documentElement.style.fontSize = `${16 * zoom}px`;
    return () => {
      document.documentElement.style.fontSize = "";
      document.documentElement.style.removeProperty("--ui-scale");
    };
  }, [zoom]);

  const fullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  return (
    <>
    <header data-testid="site-header" className="site-header">
      <div className="govt-strip">
        <div className="top-utility">
          <div className="govt-bar">
            <Landmark size={13} style={{ flexShrink: 0 }} /> <span>{t("govt")}</span>
          </div>
          <div className="a11y-row">
            <button data-testid="lang-toggle-btn" className="a11y-btn" onClick={() => setLang(lang === "en" ? "te" : "en")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.15)" }}>
              <Languages size={14} /> {lang === "en" ? "తెలుగు" : "English"}
            </button>
            <button data-testid="font-increase-btn" className="a11y-btn" onClick={() => setZoom(z => Math.min(1.2, +(z + 0.1).toFixed(1)))}>A+</button>
            <button data-testid="font-reset-btn" className="a11y-btn" onClick={() => setZoom(1)}>A</button>
            <button data-testid="font-decrease-btn" className="a11y-btn" onClick={() => setZoom(z => Math.max(0.8, +(z - 0.1).toFixed(1)))}>A−</button>
            <button data-testid="fullscreen-btn" className="a11y-btn" onClick={fullscreen} aria-label="Toggle fullscreen"><Maximize2 size={14} /></button>
          </div>
        </div>
      </div>

      <div className="brand-band glass-band">
        <div className="header-grid">
          <div className="header-side header-side-left hide-mobile">
            {dignitaries.left.map(p => <Person key={p.name} p={p} />)}
            <ApEmblem testId="ap-emblem" />
          </div>

          <Link to="/" data-testid="brand-link" className="brand-link">
            <div className="brand-mobile-marks">
              <ApEmblem />
              <BrandLogo size="auto" plate />
            </div>
            <div className="font-serif brand-title" lang={lang}>{t("title")}</div>
            <div className="brand-rule" />
            <div className="brand-sub">{t("enterprise")}</div>
            <div className="brand-act">{t("act")}</div>
          </Link>

          <div className="header-side header-side-right hide-mobile">
            <BrandLogo size="auto" plate />
            {dignitaries.right.map(p => <Person key={p.name} p={p} />)}
          </div>

          <button data-testid="mobile-menu-btn" onClick={() => setOpen(v => !v)} className="show-mobile menu-fab" aria-label="menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div className="show-mobile mobile-dignitaries" data-testid="mobile-dignitaries">
        {[...dignitaries.left, ...dignitaries.right].map(p => (
          <div key={p.name} className="mobile-person">
            <img src={p.img} alt={p.name} />
            <div className="mobile-person-name">{p.name.replace(/^Shri\s+/, "")}</div>
            <div className="mobile-person-role">{p.role}</div>
          </div>
        ))}
      </div>

    </header>

      <nav className="nav-3d nav-sticky" data-testid="main-nav">
        <div className="nav-wrap">
          {navLinks.map(l => (
            <NavLink key={l.path} to={l.path} data-testid={`nav-${l.path === "/" ? "home" : l.path.slice(1)}`} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              {t(`nav.${l.name}`)}
            </NavLink>
          ))}
        </div>
      </nav>

      {open && (
        <div className="show-mobile mobile-drawer" data-testid="mobile-drawer">
          {navLinks.map(l => (
            <NavLink key={l.path} to={l.path} className="mobile-drawer-link">
              {t(`nav.${l.name}`)}
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
}
